/* eslint-disable max-lines */
import styled, { css } from "styled-components";
import { Divider, DropdownField, FlatButton, Flex, Icon } from "@evergis/ui";

import { ChartLegendContainer } from "../components/ChartLegend/styled";
import { FeatureCardIcon } from "../styled";
import { SvgContainer } from "../components/FeatureCardSvg/styled";
import { LayerGroupList, LayerListContainer, LegendToggler } from "../../LayerList/styled";
import { FILTERED_VALUE_OPACITY } from "../constants";
import { transparentizeColor } from "../../../utils/color";

export const Container = styled(Flex)<{ isTitle?: boolean; isColumn?: boolean; isMain?: boolean }>`
  position: relative;
  width: 100%;

  ${({ isColumn }) =>
    isColumn
      ? css`
          > * {
            width: 100%;
          }
        `
      : css`
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
        `}

  ${({ isMain, isColumn }) =>
    isMain &&
    isColumn &&
    css`
      > :not(:last-child) {
        margin-bottom: 2rem;
      }
    `}

  ${({ isTitle }) =>
    isTitle &&
    css`
      &&&& {
        margin-bottom: 0.75rem;
      }
    `}
`;

export const ContainerAlias = styled(Flex)<{ hasBottomMargin?: boolean }>`
  align-items: center;
  flex-wrap: nowrap;
  margin-bottom: ${({ hasBottomMargin }) => (hasBottomMargin ? 0.25 : 0)}rem;
  font-size: 0.75rem;
  color: ${({ theme: { palette } }) => palette.textSecondary};

  span[kind] {
    margin-right: 0.5rem;

    :after {
      color: ${({ theme: { palette } }) => palette.primary};
    }
  }
`;

export const ContainerAliasIcon = styled.div`
  margin-right: 0.5rem;
`;

export const ContainerChart = styled(Flex)`
  justify-content: flex-start;

  > * {
    display: flex;
    justify-content: center;
    width: 100%;
  }
`;

export const ContainerLegend = styled(Flex)``;

export const ContainerValue = styled(Flex)<{
  fontColor?: string;
  big?: boolean;
  column?: boolean;
}>`
  align-items: center;
  flex-wrap: nowrap;
  width: 100%;
  font-size: ${({ big }) => (big ? 1.5 : 1)}rem;
  color: ${({ fontColor, theme: { palette } }) => fontColor || palette.textPrimary};

  > * {
    width: ${({ column }) => (column ? "100%" : "auto")};
  }

  ${ContainerChart}, ${ContainerLegend} {
    width: ${({ column }) => (column ? "100%" : "50%")};
  }

  ${ContainerLegend} {
    margin-left: ${({ column }) => (column ? 0 : "1rem")};
  }

  ${ChartLegendContainer} {
    flex-direction: ${({ column }) => (column ? "row" : "column")};
    margin-top: ${({ column }) => (column ? "1rem" : 0)};
  }
`;

export const ContainerUnits = styled.div`
  margin-left: 0.5rem;
  white-space: nowrap;
  font-size: 0.75rem;
`;

export const TwoColumnContainerWrapper = styled(Flex)`
  width: 100%;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;

  > * {
    flex: 1;
  }

  > ${ContainerValue} {
    justify-content: flex-end;

    > * {
      text-align: right;
    }
  }
`;

export const RoundedBackgroundContainerWrapper = styled(Flex)<{
  $center?: boolean;
  $color?: string;
  $inlineUnits?: boolean;
  $bigIcon?: boolean;
}>`
  position: relative;
  flex-direction: ${({ $bigIcon }) => ($bigIcon ? "row" : "column")};
  width: 7.75rem;
  margin-bottom: 0.5rem;
  padding: 0.75rem 0.75rem 0.5rem;
  background-color: ${({ theme: { palette } }) => palette.element};
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.large};
  flex-wrap: nowrap;

  ${({ $center }) =>
    $center &&
    css`
      align-items: center;

      * {
        display: flex;
        justify-content: center;
        text-align: center;
      }

      > * {
        width: 100%;
      }
    `};

  ${({ $color }) =>
    $color &&
    css`
      background-color: ${transparentizeColor($color, 6)};

      * {
        color: ${$color};
      }
    `};

  ${FeatureCardIcon}, ${SvgContainer} {
    margin-bottom: 0.25rem;
  }

  ${({ $bigIcon }) =>
    $bigIcon &&
    css`
      ${FeatureCardIcon}, ${SvgContainer} {
        position: absolute;
        top: 0.75rem;
        right: 0.75rem;
        width: 3rem;
        opacity: 0.12;

        :after {
          font-size: 3rem;
        }
      }
    `};

  ${ContainerValue} {
    flex-direction: ${({ $inlineUnits }) => ($inlineUnits ? "row" : "column")};
    align-items: ${({ $inlineUnits }) => ($inlineUnits ? "center" : "flex-start")};
    line-height: 1;
  }

  ${ContainerUnits} {
    margin-left: ${({ $inlineUnits }) => ($inlineUnits ? "0.25rem" : 0)};
  }

  ${ContainerAlias} {
    margin-top: 0.25rem;
  }
`;

export const ImageContainerBg = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  img {
    width: 100%;
    height: 100%;
    object-position: center;
    object-fit: cover;
  }
`;

export const ImageContainerTitle = styled.div`
  width: 100%;
  overflow-wrap: break-word;
  font-size: 1rem;
  font-weight: 500;
`;

export const ImageContainerText = styled.div`
  width: 100%;
  overflow-wrap: break-word;
  margin-top: 0.5rem;
  font-size: 0.75rem;
`;

export const ImageContainerButton = styled(FlatButton)`
  min-height: 1.5rem;
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.large};
  background-color: ${({ theme: { palette } }) => palette.primary};
  text-transform: none;

  :hover {
    background-color: ${({ theme: { palette } }) => palette.primaryDeep};
  }
`;

export const ImageContainerWrapper = styled(Flex)`
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
  padding: 1rem;
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.medium};
  overflow: hidden;

  ${ImageContainerTitle}, ${ImageContainerText}, ${ImageContainerButton} {
    z-index: 1;
    color: ${({ theme: { palette } }) => palette.textContrast};
  }

  :after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

export const IconContainerWrapper = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  position: relative;
  padding: 0.5rem 1rem 1rem;
  background-color: ${({ theme: { palette } }) => palette.element};
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.medium};
  overflow: hidden;
`;

export const IconContainerHeaderWrapper = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  width: 100%;
  margin-bottom: 0.25rem;
`;

export const IconContainerHeader = styled(Flex)`
  align-items: center;
  flex-wrap: nowrap;
  width: 100%;
  margin-right: 0.5rem;
  font-size: 0.875rem;

  ${Icon} {
    margin-right: 0.5rem;

    :after {
      font-size: 1.15rem;
      color: ${({ theme: { palette } }) => palette.textSecondary};
    }
  }
`;

export const IconContainerTitle = styled(Flex)`
  > * {
    width: 13rem;
    white-space: nowrap;
    overflow: hidden;
    font-family: "Roboto Bold", "Roboto", sans-serif;
    text-overflow: ellipsis;
  }
`;

export const IconContainerText = styled.div`
  width: 100%;
  overflow-wrap: break-word;
  font-size: 0.75rem;
  color: ${({ theme: { palette } }) => palette.textSecondary};
`;

export const PieChartCenter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  font-family: "Roboto Bold", sans-serif;
  font-size: 1.5rem;
`;

export const ContainerDivider = styled(Divider)<{ $bgColor?: string }>`
  width: 100%;
  border-color: ${({ theme: { palette }, $bgColor }) => $bgColor || palette.elementDeep};
`;

export const ContainerTitle = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  width: 100%;

  > * {
    font-size: 1.125rem;
    font-weight: 500;
  }
`;

export const ContainerIconTitle = styled(Flex)<{
  fontColor?: string;
  hasClick?: boolean;
}>`
  cursor: ${({ hasClick }) => (hasClick ? "pointer" : "default")};
  align-items: center;
  flex-wrap: nowrap;
  color: ${({ fontColor, theme: { palette } }) => fontColor || palette.textSecondary};

  img,
  span[kind],
  ${SvgContainer} {
    margin-right: 0.5rem;
  }

  ${({ fontColor }) =>
    !!fontColor &&
    css`
      span[kind] {
        color: ${fontColor};
      }

      ${SvgContainer} {
        path,
        circle {
          fill: ${fontColor};
        }
      }
    `};

  ${LegendToggler} {
    margin-left: 0.25rem;
  }
`;

export const FiltersContainerWrapper = styled(Flex)<{
  $padding?: number;
  $bgColor?: string;
  $fontSize?: string;
  $fontColor?: string;
}>`
  flex-direction: column;
  padding: ${({ $padding }) => $padding ?? "0.75rem"};
  background-color: ${({ $bgColor, $fontColor, theme: { palette } }) =>
    $bgColor || ($fontColor ? transparentizeColor($fontColor, 6) : palette.element)};
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.medium};

  ${DropdownField}, input {
    background-color: ${({ theme: { palette } }) => palette.background};
  }

  > div:not(:last-child) {
    margin-bottom: 0.5rem;
  }

  > label {
    align-items: flex-start;
    font-size: ${({ $fontSize }) => $fontSize || "0.875rem"};

    &.active {
      color: ${({ $fontColor }) => $fontColor};

      svg rect {
        fill: ${({ $fontColor }) => $fontColor} !important;
      }
    }

    &:not(:last-child) {
      margin-bottom: 0.75rem;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const ProgressContainerWrapper = styled(Flex)`
  align-items: center;
  width: 100%;
  margin-bottom: 1rem;
`;

export const ProgressIcon = styled.div`
  margin-right: 0.5rem;
`;

export const ProgressContent = styled(Flex)`
  flex: 1;
`;

export const ProgressAlias = styled(Flex)`
  flex: 1;
  justify-content: space-between;
  margin-bottom: 0.25rem;
  font-size: 0.75rem;
  color: ${({ theme: { palette } }) => palette.textSecondary};
`;

export const ProgressValue = styled(Flex)`
  align-items: center;
  font-size: 1rem;
  color: ${({ theme: { palette } }) => palette.textPrimary};
`;

export const ProgressInnerValue = styled(ProgressValue)`
  justify-content: flex-end;
  width: 4rem;
  margin-left: 0.5rem;
`;

export const ProgressUnits = styled(Flex)`
  margin-left: 0.25rem;
  font-size: 0.75rem;
  color: ${({ theme: { palette } }) => palette.textSecondary};
`;

export const ProgressBarWrapper = styled.div`
  flex-grow: 1;
  height: 0.5rem;
  background-color: ${({ theme: { palette } }) => palette.element};
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.xSmall};
`;

export const ProgressBarContainer = styled(Flex)<{ innerValue?: boolean }>`
  position: relative;
  align-items: center;
  width: 100%;

  ${({ innerValue }) =>
    innerValue &&
    css`
      ${ProgressBarWrapper} {
        height: 1.125rem;
      }

      ${ProgressInnerValue} {
        z-index: 1;
        position: absolute;
        right: 0.25rem;
      }
    `}
`;

export const ProgressBar = styled.div<{ $width?: string; $color?: string }>`
  width: ${({ $width }) => $width};
  height: inherit;
  background-color: ${({ $color }) => $color || "#a7d759"};
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.xSmall};
`;

export const ProgressTotalTitle = styled.div`
  font-size: 0.75rem;
  font-family: "Roboto Bold", sans-serif;
`;

export const ProgressTotal = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  margin-top: 1rem;
`;

export const ProgressTooltipValue = styled(Flex)`
  align-items: center;
  opacity: 0.65;
`;

export const ProgressTooltipValueOf = styled.div`
  margin: 0 0.25rem;
`;

export const DataSourceProgressContainerWrapper = styled.div<{ isLoading?: boolean }>`
  width: 100%;
  opacity: ${({ isLoading }) => (isLoading ? FILTERED_VALUE_OPACITY / 100 : 1)};
`;

export const SwiperContainer = styled.div`
  width: 100%;

  .swiper-wrapper {
    display: flex;
  }
`;

export const LayersContainerWrapper = styled(Container)<{ isPresentationMode?: boolean }>`
  ${LayerListContainer} {
    height: auto;
  }

  ${LayerGroupList} {
    margin: ${({ isPresentationMode }) => (isPresentationMode ? 0 : "0 -1.25rem 0 -1rem")};
    padding: 0;
  }
`;

export const PanoramaWrapper = styled.div`
  margin: 0 -1rem;
  border-radius: ${({ theme: { borderRadius } }) => borderRadius.medium};
  overflow: hidden;
`;
